import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { axiosObj } from "../../../../config/apiClients";
import { PhysicalPricingConfig, PhysicalProduct } from "../../../../utils/types";
import { PhysicalProductState } from "../../../types";
export interface IUpdatePhysicalProductConfigs {
  area: string;
  configs: PhysicalPricingConfig;
}

const initialState: PhysicalProductState = {
  isLoading: false,
  products: [],
  product: null,
  configs: null
}

export const getAllProducts = createAsyncThunk(
  "/physicalProducts/getAll",
  async (_, { rejectWithValue }) => {
    try {
      const response = await axiosObj.get("/physicalProducts/getAll");
      return response.data;
    } catch (error) {
      return rejectWithValue(error || { message: 'Failed to Fetch Gold Products' });
    }
  }
)

export const createProduct = createAsyncThunk(
  "/physicalProducts/create",
  async (product: PhysicalProduct, { rejectWithValue }) => {
    try {
      const response = await axiosObj.post('/physicalProducts/create', product);
      return response.data;
    } catch (error) {
      return rejectWithValue(error || { message: 'Failed to Add Gold Products' });
    }
  }
)

export const updateProduct = createAsyncThunk(
  "/physicalProducts/update",
  async (product: PhysicalProduct, { rejectWithValue }) => {
    try {
      const response = await axiosObj.patch('/physicalProducts/update', product);
      return response.data;
    } catch (error) {
      return rejectWithValue(error || { message: 'Failed to Update Product' });
    }
  }
)

export const updatePhysicalProductConfig = createAsyncThunk(
  "/physicalProducts/update/physicalProductConfigs",
  async (payload: IUpdatePhysicalProductConfigs, { rejectWithValue }) => {
    try {
      const response = await axiosObj.patch('/physicalProducts/update/physicalProductConfigs', payload);
      return response.data;
    } catch (error) {
      return rejectWithValue(error || { message: 'Failed to Update Configs' });
    }
  }
)

export const getPhysicalProductConfigs = createAsyncThunk(
  "/physicalProducts/getPhysicalProductConfigs",
  async (_, { rejectWithValue }) => {
    try {
      const response = await axiosObj.get('/physicalProducts/getPhysicalProductConfigs');
      return response.data;
    } catch (error) {
      return rejectWithValue(error || { message: 'Failed to Get Physical Pricing Config' });
    }
  }
)

export const deleteProduct = createAsyncThunk(
  "/physicalProducts/delete",
  async (productId: string, { rejectWithValue }) => {
    try {
      const response = await axiosObj.delete('/physicalProducts/delete', { data: { productId } });
      return response.data;
    } catch (error) {
      return rejectWithValue(error || { message: 'Failed to delete Gold Product' });
    }
  }
)

const physicalProductslice = createSlice({
  name: "physicalProduct",
  initialState,
  reducers: {
    setProduct: (state, action) => {
      state.product = action.payload;
    },
    removeProduct: (state, action) => {
      const productId = action.payload;
      state.products = state.products.filter(
        (product: PhysicalProduct) => product._id !== productId
      );
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(getAllProducts.pending, (state) => { state.isLoading = true; })
      .addCase(getAllProducts.fulfilled, (state, action) => {
        state.isLoading = false;
        state.products = action.payload.payload.allProducts;
      })
      .addCase(getAllProducts.rejected, (state) => {
        state.isLoading = false;
        state.products = [];
      })
      .addCase(createProduct.pending, (state) => { state.isLoading = true; })
      .addCase(createProduct.fulfilled, (state) => { state.isLoading = false; })
      .addCase(createProduct.rejected, (state) => { state.isLoading = false; })
      .addCase(updateProduct.pending, (state) => { state.isLoading = true; })
      .addCase(updateProduct.fulfilled, (state) => { state.isLoading = false; })
      .addCase(updateProduct.rejected, (state) => {
        state.isLoading = false;
        state.product = null;
      })
      .addCase(deleteProduct.pending, (state) => { state.isLoading = true; })
      .addCase(deleteProduct.fulfilled, (state) => { state.isLoading = false; })
      .addCase(deleteProduct.rejected, (state) => {
        state.isLoading = false;
        state.product = null;
      })
      .addCase(updatePhysicalProductConfig.pending, (state) => { state.isLoading = true; })
      .addCase(updatePhysicalProductConfig.fulfilled, (state) => { state.isLoading = false; })
      .addCase(updatePhysicalProductConfig.rejected, (state) => {
        state.isLoading = false;
        state.products = [];
      })
      .addCase(getPhysicalProductConfigs.pending, (state) => { state.isLoading = true; })
      .addCase(getPhysicalProductConfigs.fulfilled, (state, action) => {
        state.isLoading = false;
        state.configs = action.payload.payload; // { dubai: { XAU, XAG }, sharjah: { XAU, XAG } }
      })
      .addCase(getPhysicalProductConfigs.rejected, (state) => {
        state.isLoading = false;
        state.configs = null;
      });
  }
});

export const { setProduct, removeProduct } = physicalProductslice.actions;
export default physicalProductslice.reducer;